/**
 * Button — the primary interactive primitive.
 *
 * WHEN TO USE: any clickable action (submit, navigate, toggle).
 * WHAT NOT TO DO: don't use for navigation-only links (use <a>); don't override
 * colors inline — pick a variant; don't mix sizes in the same row.
 */
import React from 'react';
import { Loader2, type LucideIcon } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  iconLeft?: LucideIcon;
  iconRight?: LucideIcon;
  loading?: boolean;
}

const variantStyles: Record<Variant, string> = {
  primary:
    'bg-brand-600 text-white hover:bg-brand-500 active:bg-brand-700 focus-visible:ring-brand-500/50',
  secondary:
    'bg-gray-800 text-gray-200 border border-gray-700 hover:bg-gray-700 hover:border-gray-600 active:bg-gray-600 focus-visible:ring-gray-500/40',
  ghost:
    'bg-transparent text-gray-300 hover:bg-gray-800 hover:text-gray-100 active:bg-gray-700 focus-visible:ring-gray-500/40',
  danger:
    'bg-danger-600 text-white hover:bg-danger-500 active:bg-danger-700 focus-visible:ring-danger-500/50',
};

const sizeStyles: Record<Size, string> = {
  sm: 'h-8 px-3 text-ui gap-1.5',
  md: 'h-9 px-4 text-ui gap-2',
};

const iconSizeMap: Record<Size, string> = {
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  iconLeft: IconLeft,
  iconRight: IconRight,
  loading = false,
  disabled,
  children,
  className = '',
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      disabled={isDisabled}
      className={[
        'inline-flex items-center justify-center font-medium rounded-pill whitespace-nowrap',
        'transition-all duration-fast ease-out-expo',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950',
        'disabled:opacity-50 disabled:pointer-events-none',
        variantStyles[variant],
        sizeStyles[size],
        className,
      ].join(' ')}
      {...props}
    >
      {loading ? (
        <Loader2 className={`${iconSizeMap[size]} animate-spin`} />
      ) : IconLeft ? (
        <IconLeft className={iconSizeMap[size]} />
      ) : null}
      {children}
      {!loading && IconRight && <IconRight className={iconSizeMap[size]} />}
    </button>
  );
}
